import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';
import { getNetworkShows } from '@account_module/services/megaphone';
import { MegaphoneShowType } from '@account_module/types/Onboarding';
import { MEGAPHONE_INTEGRATION_QUERY_KEY } from '@account_module/constants/onBoardingKeys';

export const useMegaphoneNetworksShows = (ids: string[], token: string | null) => {
  const showsResultsByNetworks = useQueries({
    queries: ids?.map((id) => {
      return {
        queryKey: [MEGAPHONE_INTEGRATION_QUERY_KEY.SHOWS_BY_NETWORK_ID, id],
        queryFn: () => getNetworkShows({ network_id: id, token }),
        enabled: !!token,
        staleTime: 60 * 60 * 1000, // 1 hour
      };
    }),
  });

  const isLoading = useMemo(
    () => showsResultsByNetworks.some((show) => show.isLoading),
    [showsResultsByNetworks]
  );

  const allShows = useMemo(() => {
    if (!showsResultsByNetworks) return [];

    const arr: MegaphoneShowType[] = [];
    showsResultsByNetworks?.forEach(
      (network) => network?.data?.forEach((show: MegaphoneShowType) => arr.push(show))
    );
    return arr;
  }, [showsResultsByNetworks]);

  const filteredShows = useMemo(() => {
    if (!allShows) return [];

    return allShows.filter((show: MegaphoneShowType) => !show.adFree && show.mainFeed);
  }, [allShows]);

  const adFreeOrNonMainFeedShows = useMemo(() => {
    if (!allShows) return [];

    return allShows.filter((show: MegaphoneShowType) => show.adFree || !show.mainFeed);
  }, [allShows]);

  const networksWithFilteredShows = useMemo(
    () =>
      showsResultsByNetworks?.map(
        (network) =>
          network?.data?.filter((show: MegaphoneShowType) => !show.adFree && show.mainFeed)
      ) || [],
    [showsResultsByNetworks]
  );

  return {
    showsResultsByNetworks,
    isLoading,
    allShows,
    filteredShows,
    adFreeOrNonMainFeedShows,
    networksWithFilteredShows,
  };
};
